import cx from 'classnames'
import type React from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'

import type { Locale } from '@libs/typescript-utils'

import { formatDate } from '@helpers'

import { DATE_WIDGET_VARIANTS as MENU_VARIANTS, PAGE_FILTER_VARIANTS as VARIANTS } from '@components/filters/constants'
import { DatePeriodsMenu, DateTypeSelector, FilterOptions } from '@components/filters/elements'
import type { DateTypeOption } from '@components/filters/types'
import { BUTTON_CSS_VARIABLES, ButtonWithIcon, CustomDatePicker, Popover, Typography } from '@components/ui'
import { CalendarIcon } from '@components/ui/svgIcons'

import { DisabledDateFilterValueMessage } from '@messages'

const DateInputButton = styled(ButtonWithIcon)`
  padding: 10px;
  border-radius: 0;
  justify-content: flex-start;
  height: 100%;
  font-size: 12px;
  min-width: 205px;

  ${BUTTON_CSS_VARIABLES.hoverBackgroundColor}: ${({ theme }) => `${theme.colors.common.oceanBlue}10`};
  ${BUTTON_CSS_VARIABLES.hoverBorderColor}: ${({ theme }) => `${theme.colors.common.oceanBlue}10`};
  ${BUTTON_CSS_VARIABLES.color}: ${({ theme }) => theme.colors.gray[100]};

  &::before {
    border: none;
  }
`

const StyledCalendarIcon = styled(CalendarIcon)`
  color: ${({ theme }) => theme.colors.gray[40]};
`

const MinimalWrapper = styled.div`
  padding: 0;
  height: 50px;
  display: flex;
  align-items: center;
  border-left: 1px solid ${({ theme }) => theme.colors.common.paperStroke};

  &:not(.compact) {
    border-right: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
  }

  & > * {
    height: 100%;
  }

  &.compact {
    height: ${({ theme }) => theme.appBarHeightMedium}px;
  }
`

const Divider = styled.div`
  width: 1px;
  background-color: ${({ theme }) => theme.colors.common.paperStroke};
`

const DatepickerWrapper = styled.div`
  display: flex;
  padding: 10px;
  gap: 10px;
  font-size: 12px;

  & > .rw-calendar {
    width: 200px;
    padding: 5px;
  }
`

const MenuWrapper = styled.div`
  min-width: 244px;
`

const FormDatepickerWrapper = styled.div`
  display: flex;
  gap: 10px;
  font-size: 16px;
`

const CalendarContainer = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 5px;

  & .rw-calendar {
    padding: 5px;
  }

  & .rw-cell {
    transition: all 150ms ease-in-out;
  }

  & .rw-cell.rw-state-selected,
  & .rw-cell.rw-state-selected:hover {
    background-color: ${({ theme }) => theme.colors.common.oceanBlue};
    border-color: ${({ theme }) => theme.colors.common.oceanBlue};
  }

  &.disabled .rw-cell.rw-state-selected,
  &.disabled .rw-cell.rw-state-selected:hover {
    background-color: ${({ theme }) => theme.colors.gray[120]};
    border-color: ${({ theme }) => theme.colors.gray[120]};
  }

  & .rw-calendar-btn-view {
    color: ${({ theme }) => theme.colors.gray[160]};
  }
`

interface MinimalDateFilterProps {
  children: React.ReactNode
  dateTypeOptions: DateTypeOption[]
  dateTypeValue: string
  disabled: boolean
  fromDate: Nullable<string>
  isCompact?: boolean
  isDateFilterToggleDisabled?: boolean
  onChange: (values: DateFilterChangeValues) => void
  onToggle: (payload: boolean) => void
  toDate: Nullable<string>
}

function MinimalDateFilter({
  children,
  dateTypeOptions,
  dateTypeValue,
  disabled,
  fromDate,
  isCompact = false,
  isDateFilterToggleDisabled = false,
  onChange,
  onToggle,
  toDate,
}: MinimalDateFilterProps) {
  return (
    <>
      <MinimalWrapper className={cx({ compact: isCompact })}>
        <Popover
          placement="bottom-end"
          trigger={
            <DateInputButton icon={<StyledCalendarIcon size={18} />} variant="secondaryText">
              {disabled ? (
                DisabledDateFilterValueMessage
              ) : (
                <>
                  <FormattedDate value={fromDate ?? undefined} /> - <FormattedDate value={toDate ?? undefined} />
                </>
              )}
            </DateInputButton>
          }
        >
          <DatepickerWrapper>{children}</DatepickerWrapper>
        </Popover>
        <Divider />
        <FilterOptions variant="date">
          <MenuWrapper>
            <DateTypeSelector
              onChange={onChange}
              options={dateTypeOptions}
              value={dateTypeValue}
              variant={MENU_VARIANTS.menu}
            />

            <DatePeriodsMenu
              disabled={disabled}
              isDateFilterToggleDisabled={isDateFilterToggleDisabled}
              onToggle={onToggle}
              onChange={onChange}
              variant={MENU_VARIANTS.menu}
            />
          </MenuWrapper>
        </FilterOptions>
      </MinimalWrapper>
    </>
  )
}

interface DateFilterContentProps {
  disabled: boolean
  fromDate: Nullable<string>
  locale: Locale
  onChange: (values: DateFilterChangeValues) => void
  toDate: Nullable<string>
}

function DateFilterContent({ disabled, fromDate, locale, onChange, toDate }: DateFilterContentProps) {
  return (
    <>
      <CalendarContainer className={cx({ disabled })}>
        <Typography tag="h4" color="gray-80" uppercase size="700-xs">
          <FormattedMessage id="datefilter.label.fromDate" defaultMessage="dátum (-tól)" />
        </Typography>
        <CustomDatePicker
          selected={fromDate ? new Date(fromDate) : undefined}
          onChange={value => onChange({ fromDate: formatDate(value) })}
          locale={locale}
          maxDate={toDate ? new Date(toDate) : undefined}
          inline
          fixedHeight
        />
      </CalendarContainer>
      <CalendarContainer className={cx({ disabled })}>
        <Typography tag="h4" color="gray-80" uppercase size="700-xs">
          <FormattedMessage id="datefilter.label.toDate" defaultMessage="dátum (-ig)" />
        </Typography>
        <CustomDatePicker
          selected={toDate ? new Date(toDate) : undefined}
          onChange={value => onChange({ toDate: formatDate(value) })}
          minDate={fromDate ? new Date(fromDate) : undefined}
          locale={locale}
          inline
          fixedHeight
        />
      </CalendarContainer>
    </>
  )
}

export interface DateFilterProps extends DateFilterContentProps {
  dateTypeOptions: DateTypeOption[]
  dateTypeValue: string
  isCompact?: boolean
  isDateFilterToggleDisabled?: boolean
  onToggle: (payload: boolean) => void
  variant?: ConstObjectValues<typeof VARIANTS>
}

function PureDateFilter({
  dateTypeOptions,
  dateTypeValue,
  disabled,
  fromDate,
  isDateFilterToggleDisabled = false,
  onChange,
  onToggle,
  toDate,
  variant = VARIANTS.widget,
  ...rest
}: DateFilterProps) {
  if (variant === VARIANTS.form) {
    return (
      <FormDatepickerWrapper>
        <DateFilterContent disabled={disabled} fromDate={fromDate} onChange={onChange} toDate={toDate} {...rest} />
      </FormDatepickerWrapper>
    )
  }

  return (
    <MinimalDateFilter
      dateTypeOptions={dateTypeOptions}
      dateTypeValue={dateTypeValue}
      disabled={disabled}
      fromDate={fromDate}
      isCompact={variant === VARIANTS.compact}
      isDateFilterToggleDisabled={isDateFilterToggleDisabled}
      onChange={onChange}
      onToggle={onToggle}
      toDate={toDate}
    >
      <DateFilterContent disabled={disabled} fromDate={fromDate} onChange={onChange} toDate={toDate} {...rest} />
    </MinimalDateFilter>
  )
}

function mapStateToProps(state: Store) {
  return {
    locale: state.locale.language,
  }
}

export const DateFilter = connect(mapStateToProps)(PureDateFilter)

DateFilter.displayName = 'DateFilter'
